import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { useLoader } from "@/app/hooks/useLoader";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { GiMedicalThermometer } from "react-icons/gi";
import DataTable from "@/app/components/ui/Reusable/DataTable";
import SkeletonType1 from "@/app/components/ui/Skeletons/Type1";
import SolidButton from "@/app/components/ui/Buttons/SolidButton";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import InputWithAddOn from "@/app/components/ui/Inputs/InputWithAddOn";
import { IConfigurationListState } from "@/app_redux/reducers/slices/auth/configuration_slice";
import { useConfigurationModuleSearchAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationModuleSearchAPI";
import { useConfigurationEquipmentListAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationEquipmentListAPI";
import { getEquipmentColumns } from "@/app/hooks/custom_hooks";

const ConfigurationEquipmentSetup = () => {
  const hasFetchedRef = useRef(false);
  const { NO_APPOINTMENTS } = useImageConstants();
  const isEquipmentListInProgress = useLoader("isOnboardEquipmentListInProgress");
  const { getRouteKey } = useRouter();
  const navigate = useNavigate();
  const fetchEquipmentList = useConfigurationEquipmentListAPI();
  const routeIdConfig = getRouteKey("CONFIGURATION", "id");
  const { routeState } = useRouteInfo(routeIdConfig);
  const { equipmentList: equipmentListData } = routeState.state as IConfigurationListState;
  const searchEquipment = useConfigurationModuleSearchAPI("equipment");
  const columns = getEquipmentColumns(navigate, getRouteKey);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      fetchEquipmentList();
      hasFetchedRef.current = true;
    }
  }, []);

  return (
    <div className="bg-white px-5 py-3 rounded-md">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="font-semibold text-base text-gray-900 leading-6">Equipment Setup List</h1>
          <p className="mt-2 text-gray-700 text-sm">
            A list of all the equipments associated with the respective services and sub services.
          </p>
        </div>
        <div className="sm:flex-none mt-4 sm:mt-0 sm:ml-16">
          <SolidButton asLink href={getRouteKey("EQUIPMENT_SETUP_ONBOARD", "url")} size="XL">
            Onboard New Equipment
          </SolidButton>
        </div>
      </div>
      <div>
        <InputWithAddOn
          onEnterKeyPress={(query: string) => {
            if (query.length === 0) {
              fetchEquipmentList();
            } else {
              searchEquipment(query);
            }
          }}
          containerClassName="w-1/2"
          addon={<GiMedicalThermometer className="w-4 h-4" />}
          placeholder="Search Equipments"
        />
      </div>
      <div className="mt-8 flow-root">
        {isEquipmentListInProgress ? (
          <div className="mt-8">
            <SkeletonType1 />
            <SkeletonType1 className="mt-4" />
          </div>
        ) : equipmentListData?.length > 0 ? (
          <div>
            <DataTable data={equipmentListData || []} columns={columns} maxHeight="600px" />
          </div>
        ) : (
          <NoDataFoundComponent
            imageURL={NO_APPOINTMENTS}
            message="No Onboarded Equipments"
            description="There are no onboarded equipments, please onboard a few."
          />
        )}
      </div>
    </div>
  );
};

export default ConfigurationEquipmentSetup;
